import { Lightbox } from '@app/components/various/Lightbox/Lightbox';
import { Portal } from '@app/components/various/Portal';
import { useGallery } from '@app/utils/hooks/useGallery';
import { AnyLightboxMedia, AnyMedia, Product } from '@typings';
import React from 'react';
import { CSSTransition } from 'react-transition-group';

import { useProductDetailsLayoutContext } from '../context/ProductDetailsLayoutContext';
import { useProductDetailsVariantContext } from '../context/ProductDetailsVariantContext';

import { ProductDetailsGalleryDots } from './ProductDetailsGalleryDots';
import { ProductDetailsMedia } from './ProductDetailsMedia';

type VariantName = {
  [variantNumber: string]: string;
};

interface Props {
  productDetails: Product.Full;
  medias: AnyMedia[];
  isLightboxOpen: boolean;
  setIsLightboxOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export const ProductDetailsGallery = ({ productDetails, medias, isLightboxOpen, setIsLightboxOpen }: Props) => {
  const { handleMediaIndexChange, activeMediaIndex } = useProductDetailsVariantContext();
  const { isDesktop, isTablet } = useProductDetailsLayoutContext();
  const { ref } = useGallery({
    activeIndex: activeMediaIndex,
    enabled: !isLightboxOpen,
    onChange: handleMediaIndexChange,
    orientation: isDesktop ? 'vertical' : 'horizontal',
    slidesCount: isTablet ? 2 : 1,
  });
  const relatedVariants = productDetails.relatedProducts.variants.reduce(
    (acc, variant) => ({ ...acc, [variant.variant]: variant.variantName }),
    {},
  );

  const allVariants: VariantName = {
    [productDetails.primaryVariant]: productDetails.variantName,
    ...relatedVariants,
  };

  const handleOpenLightbox = React.useCallback(() => {
    setIsLightboxOpen(true);
  }, [setIsLightboxOpen]);

  const handleCloseLightbox = React.useCallback(() => {
    setIsLightboxOpen(false);
  }, [setIsLightboxOpen]);

  const lighboxMedias: AnyLightboxMedia[] = medias.map(media => ({
    ...media,
    label: allVariants[media.variantId] ?? allVariants[productDetails.primaryVariant],
  }));

  return (
    <>
      <div className="h-full w-full bg-neutral-96">
        <div
          ref={ref}
          key={productDetails.product}
          className="flex h-full w-full snap-x snap-mandatory overflow-y-auto no-scrollbar focus-visible:focus-outline lg:snap-y lg:flex-col"
        >
          {medias.map((media, index) => {
            return (
              <ProductDetailsMedia
                key={index}
                media={media}
                onClick={handleOpenLightbox}
                className="mx-auto w-full shrink-0 snap-start sm:w-1/2 lg:w-full"
              />
            );
          })}
        </div>
        <ProductDetailsGalleryDots medias={medias} activeMediaIndex={activeMediaIndex} />
      </div>
      <Portal>
        <CSSTransition
          in={isLightboxOpen}
          timeout={150}
          classNames={{
            enter: 'opacity-0',
            enterActive: '!opacity-100 transition-[opacity,transform]',
            exit: 'opacity-100',
            exitActive: '!opacity-0 transition-[opacity,transform]',
          }}
          unmountOnExit
        >
          {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
          <Lightbox
            isOpen={isLightboxOpen}
            medias={lighboxMedias}
            currentIndex={activeMediaIndex}
            onChange={handleMediaIndexChange}
            onClose={handleCloseLightbox}
          />
        </CSSTransition>
      </Portal>
    </>
  );
};
